import React from "react";
import type { ListActionsProps, Record } from "react-admin";
import {
	ImageField,
	List,
	Datagrid,
	TextField,
	ShowButton,
	Show,
	SimpleShowLayout,
	FileField,
	Filter,
	TextInput,
	DateInput,
	ListActions,
	DeleteButton,
	Button,
	useRedirect,
	useNotify,
	FunctionField,
} from "react-admin";
import { useMutation } from "@apollo/client/react";
import { gql } from "@apollo/client";
import ShoppingCart from "@material-ui/icons/ShoppingCart";
import UserSummaryField from "~/components/user-summary-field.tsx";
import PictureSummaryField from "~/components/picture-summary-field.tsx";
import LoadMorePagination from "~/components/load-more-pagination.tsx";
import ListThumbnailField from "~/components/list-thumbnail-field.tsx";
import ChangeUserButton from "~/components/change-user-button.tsx";
import ExportReportButton from "~/components/export-report-button.tsx";
import userSummaryFragment from "~/api/user-summary-fragment.ts";
import removeTypenames from "~/utils/remove-typenames.ts";
import TimezoneDateField from "~/components/date-field.tsx";

function SavedProjectFilter(props: any) {
	return (
		<Filter {...props}>
			<TextInput label="User email address" source="emailAddress" alwaysOn />
			<DateInput label="From date" source="fromDate" />
			<DateInput label="To date" source="toDate" />
		</Filter>
	);
}

type OrderProjectVariables = {
	readonly id: string;
};

function OrderSavedProjectButton({ record }: any) {
	const notify = useNotify();
	const redirect = useRedirect();
	const [orderProject, { loading }] = useMutation<
		unknown,
		OrderProjectVariables
	>(
		gql`
			mutation orderSavedProjectMutation($id: ID!) {
				orderSavedProject(id: $id) {
					__typename
					id
				}
			}
		`,
		{
			variables: { id: record.id },
			onError(e) {
				notify(e.message, "error");
			},
			onCompleted() {
				notify("Project has been ordered", "info");
				redirect("list", "/orderedProjects");
			},
		},
	);
	return (
		<Button
			type="button"
			onClick={() => orderProject()}
			label={loading ? "Ordering" : "Order"}
			disabled={loading}
		>
			<ShoppingCart />
		</Button>
	);
}

function ChangeSavedProjectUserButton({ record }: any) {
	return (
		<ChangeUserButton
			record={record}
			mutation={gql`
				mutation changeSavedProjectUserMutation(
					$id: ID!
					$email: String!
					$copy: Boolean!
				) {
					changeSavedProjectUser(id: $id, email: $email, copy: $copy) {
						__typename
						id
						user {
							__typename
							id
							...userSummary
						}
					}
				}
				${userSummaryFragment}
			`}
			useCopy
		/>
	);
}

function SavedProjectListActions(props: ListActionsProps) {
	return (
		<div
			style={{ display: "flex", flexDirection: "row", alignItems: "center" }}
		>
			<ListActions {...props} />
			<ExportReportButton
				mutation={gql`
					mutation createSavedProjectsReportMutation {
						createSavedProjectsReport
					}
				`}
			/>
		</div>
	);
}

function SavedProjectList(props: any) {
	return (
		<List
			{...props}
			title="Saved projects"
			bulkActionButtons={false}
			filters={<SavedProjectFilter />}
			sort={{ field: "updatedAt", order: "DESC" }}
			pagination={<LoadMorePagination />}
			exporter={false}
			actions={<SavedProjectListActions />}
		>
			<Datagrid>
				<TextField source="id" sortable={false} />
				<TextField label="User" source="user.emailAddress" sortable={false} />
				<TextField
					label="User ip address"
					source="userIpAddress"
					sortable={false}
				/>
				<TimezoneDateField source="updatedAt" showTime />
				<ListThumbnailField source="mediumImageUrl" sortable={false} />
				<ShowButton />
				<OrderSavedProjectButton />
				<ChangeSavedProjectUserButton />
				<DeleteButton undoable={false} />
			</Datagrid>
		</List>
	);
}

function SavedProjectShow(props: any) {
	return (
		<Show {...props}>
			<SimpleShowLayout>
				<TextField source="id" />
				<TimezoneDateField source="createdAt" showTime />
				<TimezoneDateField source="updatedAt" showTime />
				<FileField
					source="sourceImageUrl"
					target="_blank"
					rel="noopener noreferrer"
					title="Source image"
				/>
				<FileField
					source="backgroundMaskImageUrl"
					target="_blank"
					rel="noopener noreferrer"
					title="Background mask image"
				/>
				<FileField
					source="colorisationImageUrl"
					target="_blank"
					rel="noopener noreferrer"
					title="Colorisation image"
				/>
				<FileField
					source="enhanceFacesImageUrl"
					target="_blank"
					rel="noopener noreferrer"
					title="Enhance faces image"
				/>
				<FunctionField
					{...props}
					source="faceBoundingBoxes"
					render={(record?: Record) => {
						if (!record) {
							return <div />;
						}

						return (
							<div>
								{JSON.stringify(removeTypenames(record.faceBoundingBoxes))}
							</div>
						);
					}}
				/>
				<ImageField source="mediumImageUrl" />
				<PictureSummaryField label="Picture" source="currentPicture" />
				<TextField label="User ip address" source="userIpAddress" />
				<UserSummaryField label="User" source="user" />
			</SimpleShowLayout>
		</Show>
	);
}

export { SavedProjectShow, SavedProjectList };
